import { type FC, useState, useContext } from 'react';
import { useFragment, graphql } from 'react-relay';
import { utils } from 'ethers';
import { FormattedMessage, FormattedNumber, defineMessages, useIntl } from 'dibs-react-intl';
import { Button } from 'dibs-elements/exports/Button';
import { ModalBody } from 'dibs-elements/exports/ModalBody';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import { PlaceBidCountdown } from './PlaceBidCountdown';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import { useWeb3Connection } from 'dibs-crypto-utils/exports/useWeb3Connection';
import { shortAddress } from 'dibs-crypto-utils/exports/formattingUtils';
import { EthereumAmountInput } from 'dibs-crypto-utils/exports/EthereumAmountInput';
import { useBidPrices } from '../../helpers/useBidPrices';
import { NftPdpContractContext } from '../../NftPdpContractContext/NftPdpContractContext';
import { NftPdpBidModalContext } from '../NftPdpBidModalContext';
import { addParams } from 'dibs-crypto-utils/exports/imageOptimizationParams';

import { type PlaceBidContent_nftItem$key } from './__generated__/PlaceBidContent_nftItem.graphql';

import styles from './PlaceBidContent.scss';

const messages = defineMessages({
    bidInputTitle: {
        id: 'nft.pdp.bidModal.bidInputTitle',
        defaultMessage: 'Bid Amount in Ether (must be a number)',
    },
});

const nftItemFragment = graphql`
    fragment PlaceBidContent_nftItem on NftItem {
        mostRecentAuction {
            ...useBidPrices_nftAuction
        }
        etherRates {
            ...EthereumAmountInput_etherRates
        }
        creatorAddress {
            publicProfile {
                profileName
            }
        }
        metadata {
            ... on NftDigitalArtMetadata {
                name
                media {
                    webUrl
                    posterFrameUrl
                }
            }
        }
    }
`;

type Props = { nftItem: PlaceBidContent_nftItem$key };

export const PlaceBidContent: FC<Props> = ({ nftItem: nftItemRef }) => {
    const intl = useIntl();
    const { buyerPremium, isV2AuctionContract } = useContext(NftPdpContractContext);
    const {
        placeBid,
        getConvertedEther,
        getAmountStrings,
        bidError,
        walletBalance,
        formattedWalletBalance,
        isBidInFlight,
        currentBidWei,
        auctionEndDate: endDate,
    } = useContext(NftPdpBidModalContext);

    const nftItem = useFragment(nftItemFragment, nftItemRef);
    const { mostRecentAuction, metadata, creatorAddress, etherRates } = nftItem || {};
    const creatorName = creatorAddress?.publicProfile?.profileName;
    const { name, media } = metadata || {};
    const { webUrl, posterFrameUrl } = media || {};

    const { account } = useWeb3Connection();

    const hasCurrentBid = !!currentBidWei;

    const { baseEther, minEther, minWei } = useBidPrices({
        nftAuction: mostRecentAuction,
    });

    const { etherWithConversion: basePriceLabel } = getAmountStrings(baseEther || '');

    const { etherWithConversion: minBidLabel, etherNoSymbol: minBidRounded } = getAmountStrings(
        `${minEther}`
    );
    const [bidAmount, setBidAmount] = useState<string>('');

    const isBidGreaterThanMin =
        !!bidAmount && utils.parseEther(bidAmount === '.' ? '0' : bidAmount).gte(minWei);

    let totalAmount: string = bidAmount;
    const royaltyBasis = isV2AuctionContract ? 10000 : 100;
    if (isBidGreaterThanMin) {
        totalAmount = utils.formatEther(
            utils
                .parseEther(bidAmount)
                .mul(royaltyBasis + buyerPremium)
                .div(royaltyBasis)
        );
    }
    const { etherWithConversion: totalAmountLabel } = getAmountStrings(totalAmount || '0');

    return (
        <>
            <HeadingLevel>
                {Header => (
                    <div className={styles.headerWrapper}>
                        <Header className={styles.header}>
                            <FormattedMessage
                                defaultMessage="Place a Bid"
                                id="nft.pdp.bidModal.header"
                            />
                        </Header>
                        {endDate && (
                            <div className={styles.subheader}>
                                <PlaceBidCountdown endDate={endDate} />
                            </div>
                        )}
                    </div>
                )}
            </HeadingLevel>

            <div className={styles.itemDetails}>
                <img
                    className={styles.itemImage}
                    src={addParams(posterFrameUrl || webUrl, styles.MEDIA_WIDTH) || ''}
                    alt=""
                />
                <div className={styles.itemDetailsText}>
                    <span>{creatorName}</span>
                    <strong>{name}</strong>
                </div>
            </div>
            <ModalBody className={styles.placeBidBody}>
                <dl className={styles.detailList}>
                    <div className={styles.detailWrapper}>
                        <dt>
                            <strong>
                                {hasCurrentBid ? (
                                    <FormattedMessage
                                        defaultMessage="Current Bid"
                                        id="nft.pdp.bidModal.currentBid"
                                    />
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="Reserve Price"
                                        id="nft.pdp.bidModal.reservePrice"
                                    />
                                )}
                            </strong>
                        </dt>
                        <dd>{basePriceLabel}</dd>
                    </div>
                    {hasCurrentBid && (
                        <div className={styles.detailWrapper}>
                            <dt>
                                <strong>
                                    <FormattedMessage
                                        defaultMessage="Minimum Bid"
                                        id="nft.pdp.bidModal.minimumBid"
                                    />
                                </strong>
                            </dt>
                            <dd>{minBidLabel}</dd>
                        </div>
                    )}
                </dl>
                <EthereumAmountInput
                    etherRates={etherRates?.filter(filterNulls) ?? null}
                    minValue={minBidRounded}
                    value={bidAmount}
                    onChange={e => {
                        setBidAmount(e.target.value);
                    }}
                    errorMessage={bidError}
                    disabled={isBidInFlight}
                    title={intl.formatMessage(messages.bidInputTitle)}
                    autoFocus
                />
                <Button
                    fullWidth
                    className={styles.placeBidButton}
                    onClick={() => placeBid({ bidAmount, totalAmount })}
                    disabled={!isBidGreaterThanMin || isBidInFlight}
                >
                    <FormattedMessage
                        defaultMessage="Place A Bid"
                        id="nft.pdp.bidModal.placeBidCta"
                    />
                </Button>
                {buyerPremium ? (
                    <>
                        <div className={styles.detailWrapper}>
                            <dt>
                                <strong>
                                    <FormattedMessage
                                        defaultMessage="Total Amount*"
                                        id="nft.pdp.bidModal.totalAmount"
                                    />
                                </strong>
                            </dt>
                            <dd>{totalAmountLabel}</dd>
                        </div>
                        <p>
                            <FormattedMessage
                                defaultMessage="*Includes a buyer's premium of {amount} and excludes gas fees that will also apply. No other payment methods are accepted. Bids may not be withdrawn once placed. Funds will be escrowed until either you are outbid or win."
                                id="nft.pdp.bidModal.buyerPremium1"
                                values={{
                                    amount: (
                                        <FormattedNumber
                                            style="percent"
                                            value={buyerPremium / royaltyBasis}
                                        />
                                    ),
                                }}
                            />
                        </p>
                    </>
                ) : (
                    <p>
                        <FormattedMessage
                            defaultMessage="This amount will be withdrawn from your connected wallet. No other payment methods are accepted. Bids may not be withdrawn once placed."
                            id="nft.pdp.bidModal.copy1"
                        />
                    </p>
                )}
            </ModalBody>
            <div className={styles.modalFooter}>
                <strong>
                    {shortAddress(account || '')}{' '}
                    <FormattedMessage
                        defaultMessage="Wallet Connected"
                        id="nft.pdp.bidModal.walletConnected"
                    />
                </strong>
                <div>{`${formattedWalletBalance} (${getConvertedEther(walletBalance || '')})`}</div>
            </div>
        </>
    );
};
